import { planLeadsReconciliation } from '../../../netlify/functions/_shared/prospecting-reconcile.mjs'
import type { ProspectingDataset } from '../../types/prospecting'

export type ProspectingSyncResult = {
  appended: number
  scanned: number
  skipped: number
}

export type PendingReconciliation = {
  count: number
  // Stable fingerprint of exactly which prospects are missing, used to make sure
  // one dataset is never synced twice.
  signature: string
}

/**
 * Which prospects the browser can already tell are missing from Leads. This is
 * only a trigger heuristic — the server re-reads the workbook and re-runs the
 * same planner before writing anything.
 */
export function pendingLeadsReconciliation(dataset: ProspectingDataset): PendingReconciliation {
  const plan = planLeadsReconciliation(dataset.lists)
  const signature = plan.appends
    .map((append) => `${append.list}:${append.rowNumber}`)
    .sort()
    .join('|')
  return { count: plan.appends.length, signature }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

/**
 * Appends the missing prospects to the Leads master list through the secure
 * server-side Sheets path. Never writes from the browser and never sends the
 * rows themselves — the function reads authoritative state itself.
 */
export async function syncProspectingLeads(signal?: AbortSignal): Promise<ProspectingSyncResult> {
  const response = await fetch('/api/prospecting/sync', {
    method: 'POST',
    credentials: 'include',
    headers: { accept: 'application/json', 'content-type': 'application/json' },
    body: '{}',
    signal,
  })

  const contentType = response.headers.get('content-type') ?? ''
  if (!contentType.toLowerCase().includes('application/json')) {
    throw new Error(`The Leads reconciliation service returned ${response.status}.`)
  }

  const body: unknown = await response.json()
  if (!response.ok) {
    const code = isRecord(body) && typeof body.code === 'string' ? body.code : 'PROSPECTING_SYNC_FAILED'
    throw new Error(code)
  }

  const result = isRecord(body) && isRecord(body.result) ? body.result : {}
  return {
    appended: typeof result.appended === 'number' ? result.appended : 0,
    scanned: typeof result.scanned === 'number' ? result.scanned : 0,
    skipped: typeof result.skipped === 'number' ? result.skipped : 0,
  }
}
